import { useState } from 'react';
import { Archive } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { ApiError } from '@/lib/api';
import { useArchiveMember } from '@/pages/team/hooks/useTeamQueries';
import type { UserMember } from '@/pages/team/types';

interface ArchiveMemberDialogProps {
  member: UserMember | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/**
 * Подтверждение архивации (AK-365) — насовсем, реактивации из архива нет.
 * Действие = POST /users/members/{id}/archive. История сохраняется, но
 * участник исчезает из активного списка и вернуть его нельзя.
 */
export function ArchiveMemberDialog({ member, open, onOpenChange }: ArchiveMemberDialogProps) {
  const archive = useArchiveMember();
  const [error, setError] = useState<string | undefined>();

  if (!member) return null;

  const displayName =
    member.display_name ||
    [member.first_name, member.last_name].filter(Boolean).join(' ') ||
    member.email;

  const submit = async () => {
    setError(undefined);
    try {
      await archive.mutateAsync(member.member_id);
      onOpenChange(false);
    } catch (e) {
      setError(e instanceof ApiError ? e.detail : 'Не удалось архивировать');
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[420px]">
        <DialogHeader>
          <span className="mb-1 flex h-10 w-10 items-center justify-center rounded-lg bg-destructive/15 text-destructive">
            <Archive className="h-5 w-5" />
          </span>
          <DialogTitle>Отправить {displayName} в архив?</DialogTitle>
          <DialogDescription>
            Это действие насовсем — вернуть участника из архива нельзя.
          </DialogDescription>
        </DialogHeader>

        <ul className="list-disc space-y-1 pl-5 text-sm text-muted-foreground">
          <li>Участник исчезнет из активного списка команды</li>
          <li>Доступ снимается немедленно — активные сессии завершатся</li>
          <li>Данные, поля и история его действий сохраняются</li>
        </ul>

        {error && (
          <Alert variant="destructive">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        <DialogFooter>
          <Button
            variant="ghost"
            onClick={() => onOpenChange(false)}
            disabled={archive.isPending}
          >
            Отмена
          </Button>
          <Button
            variant="destructive"
            onClick={submit}
            disabled={archive.isPending}
          >
            {archive.isPending ? 'Архивируем…' : 'В архив'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
